import { ActivityEventCard as Shared } from "@/components/activity/ActivityEventCard"
import { ClipStatusBadge } from "@/components/clips/ClipStatusBadge"
import type { ClipEvent, ClipStatus } from "@/components/clips/types"
import type { CurrentUser } from "@/types/inertia"

const AUTHOR_LABEL: Record<ClipEvent["author_type"], string> = {
  user: "You",
  agent: "Agent",
  system: "System",
}

export function ClipActivityEventCard({
  event,
  clipId,
  currentUser,
}: {
  event: ClipEvent
  clipId: number
  currentUser: CurrentUser
}) {
  return (
    <Shared
      event={event}
      eventsPath={`/clips/${clipId}/events`}
      currentUser={currentUser}
      renderSummary={renderClipEventSummary}
    />
  )
}

export function renderClipEventSummary(event: ClipEvent) {
  const author = AUTHOR_LABEL[event.author_type]

  if (event.event_type === "commented") {
    return (
      <>
        <strong className="text-ink-display">{author}</strong>{" "}
        <span className="text-ink-muted">commented</span>
      </>
    )
  }

  if (event.event_type === "created") {
    return (
      <>
        <strong className="text-ink-display">{author}</strong> created this clip
      </>
    )
  }

  if (event.event_type === "status_changed") {
    const from = (event.metadata.from as string | undefined) ?? null
    const to = (event.metadata.to as string | undefined) ?? null
    return (
      <>
        <strong className="text-ink-display">{author}</strong> changed status{" "}
        {from && (
          <>
            from <ClipStatusBadge status={from as ClipStatus} />{" "}
          </>
        )}
        {to && (
          <>
            to <ClipStatusBadge status={to as ClipStatus} />
          </>
        )}
      </>
    )
  }

  if (event.event_type === "field_updated") {
    const label = (event.metadata.label as string | undefined) ?? (event.metadata.field as string | undefined) ?? "field"
    return (
      <>
        <strong className="text-ink-display">{author}</strong> updated{" "}
        <span className="font-medium">{label}</span>
      </>
    )
  }

  if (event.event_type === "marked_for_enrichment") {
    return (
      <>
        <strong className="text-ink-display">{author}</strong> queued for enrichment
      </>
    )
  }

  if (event.event_type === "agent_review_marked") {
    return (
      <>
        <strong className="text-ink-display">{author}</strong> marked agent enrichment as reviewed
      </>
    )
  }

  if (event.event_type === "agent_review_auto_marked") {
    return (
      <>
        <strong className="text-ink-display">{author}</strong> auto-marked as reviewed{" "}
        <span className="text-ink-muted">(14-day window)</span>
      </>
    )
  }

  return (
    <>
      <strong className="text-ink-display">{author}</strong>{" "}
      <span className="text-ink-muted">{event.event_type.replace(/_/g, " ")}</span>
    </>
  )
}
